import { CallApi } from "../../saga/callApi";
import { ACCOUNTING_SYSTEM_API_BASE_URL } from "../constants";

export type SyncStatus = "PENDING" | "SYNCED" | "FAILED" | "SKIPPED";

export interface PayerW9Sync {
    existing: boolean;
    fileName: string;
    status: SyncStatus | null;
    syncedAt: string;
    syncUpdatedAt: string;
    syncError: string;
}

export interface EligibleCount {
    count: number;
    withEmail: number;
}

export interface SyncTimeLabel {
    key: "w9.synced_at" | "w9.queued_at" | "w9.failed_at" | "w9.checked_at";
    time: string;
}

export interface W9Upload {
    fileId: number;
    fileName: string;
}

interface ApiResponseEnvelope<T> {
    data?: {
        data?: T;
    } | T;
    message?: string;
}

interface PayerSettingsData {
    existing?: boolean;
    settings?: {
        w9_file_name?: string;
        w9_invoice_sync_status?: SyncStatus;
        w9_invoice_synced_at?: string;
        w9_invoice_sync_updated_at?: string;
        w9_invoice_sync_error?: string;
    };
    defaults?: Record<string, unknown>;
}

interface UploadData {
    file_id?: number;
    w9_file_id?: number;
    filename?: string;
    w9_file_name?: string;
}

interface CountData {
    count?: number;
    withEmail?: number;
}

interface ResendData {
    status?: SyncStatus | string;
}

const BASE_URL = ACCOUNTING_SYSTEM_API_BASE_URL || "";

function errorMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === "string") return err;
    if (err && typeof err === "object") {
        const obj = err as Record<string, unknown>;
        const resp = obj.response as Record<string, unknown> | undefined;
        const data = (obj.data || resp?.data) as Record<string, unknown> | string | undefined;
        if (typeof data === "string") return data;
        if (data && typeof data === "object") {
            if (typeof data.message === "string") return data.message;
            if (typeof data.detail === "string") return data.detail;
            if (typeof data.error === "string") return data.error;
        }
        if (typeof obj.message === "string") return obj.message;
    }
    return "An unexpected error occurred.";
}

export async function getPayerSettings(branchId: number): Promise<PayerW9Sync> {
    try {
        const url = `${BASE_URL}/api/v1/tax_1099/settings?branch_id=${branchId}`;
        const res = (await CallApi.GET(url, {}, true)) as ApiResponseEnvelope<PayerSettingsData>;
        const payload = (res?.data && typeof res.data === "object" && "data" in res.data ? res.data.data : res?.data) as PayerSettingsData | undefined;
        const settings = payload?.settings;
        return {
            existing: Boolean(payload?.existing),
            fileName: settings?.w9_file_name || "",
            status: settings?.w9_invoice_sync_status || null,
            syncedAt: settings?.w9_invoice_synced_at ? String(settings.w9_invoice_synced_at) : "",
            syncUpdatedAt: settings?.w9_invoice_sync_updated_at ? String(settings.w9_invoice_sync_updated_at) : "",
            syncError: settings?.w9_invoice_sync_error ? String(settings.w9_invoice_sync_error) : "",
        };
    } catch (err: unknown) {
        throw new Error(errorMessage(err));
    }
}


export async function uploadPayerW9(branchId: number, file: File): Promise<W9Upload> {
    try {
        const url = `${BASE_URL}/api/v1/tax_1099/settings/w9?branch_id=${branchId}`;
        const formData = new FormData();
        formData.append("file", file);
        const res = (await CallApi.POST(url, formData, true)) as ApiResponseEnvelope<UploadData>;
        const payload = (res?.data && typeof res.data === "object" && "data" in res.data ? res.data.data : res?.data) as UploadData | undefined;
        return {
            fileId: Number(payload?.file_id ?? payload?.w9_file_id ?? 0),
            fileName: payload?.filename ?? payload?.w9_file_name ?? file.name,
        };
    } catch (err: unknown) {
        throw new Error(errorMessage(err));
    }
}

export async function savePayerSettings(branchId: number, fileId: number | null): Promise<void> {
    try {
        const url = `${BASE_URL}/api/v1/tax_1099/settings?branch_id=${branchId}`;
        let payload: Record<string, unknown> = {};
        try {
            const current = (await CallApi.GET(url, {}, true)) as ApiResponseEnvelope<PayerSettingsData>;
            const curData = (current?.data && typeof current.data === "object" && "data" in current.data ? current.data.data : current?.data) as PayerSettingsData | undefined;
            if (curData?.settings) {
                payload = { ...curData.settings };
            } else if (curData?.defaults) {
                payload = { ...curData.defaults };
            }
        } catch {
            // defaults fallback ignored
        }
        if (fileId !== null) {
            payload.w9_file_id = fileId;
        }
        await CallApi.PUT(url, payload, true);
    } catch (err: unknown) {
        throw new Error(errorMessage(err));
    }
}

export async function getEligibleCount(branchId: number): Promise<EligibleCount> {
    try {
        const url = `${BASE_URL}/api/v1/tax_1099/settings/w9/eligible-count?branch_id=${branchId}`;
        const res = (await CallApi.GET(url, {}, true)) as ApiResponseEnvelope<CountData | number>;
        const payload = res?.data && typeof res.data === "object" && "data" in res.data ? res.data.data : res?.data;
        if (typeof payload === "number") return { count: payload, withEmail: payload };
        if (payload && typeof payload === "object") {
            const data = payload as CountData;
            const count = typeof data.count === "number" ? data.count : 0;
            const withEmail = typeof data.withEmail === "number" ? data.withEmail : count;
            return { count, withEmail };
        }
        return { count: 0, withEmail: 0 };
    } catch (err: unknown) {
        throw new Error(errorMessage(err));
    }
}

export async function resendW9(branchId: number): Promise<string> {
    try {
        const url = `${BASE_URL}/api/v1/tax_1099/settings/w9/resend?branch_id=${branchId}`;
        const res = (await CallApi.POST(url, {}, true)) as ApiResponseEnvelope<ResendData | string>;
        const payload = res?.data && typeof res.data === "object" && "data" in res.data ? res.data.data : res?.data;
        if (typeof payload === "string") return payload;
        if (payload && typeof payload === "object" && "status" in payload && typeof payload.status === "string") {
            return payload.status;
        }
        return "PENDING";
    } catch (err: unknown) {
        throw new Error(errorMessage(err));
    }
}

/**
 * Picks the timestamp that matches the sync status. synced_at is the last
 * successful sync; sync_updated_at is when the status last changed. A
 * PENDING or FAILED row must not show the old success time.
 */
export function syncTimeLabel(status: SyncStatus | null, syncedAt: string, syncUpdatedAt: string): SyncTimeLabel | null {
    if (status === "SYNCED") return syncedAt ? { key: "w9.synced_at", time: syncedAt } : null;
    if (!syncUpdatedAt) return null;
    if (status === "PENDING") return { key: "w9.queued_at", time: syncUpdatedAt };
    if (status === "FAILED") return { key: "w9.failed_at", time: syncUpdatedAt };
    if (status === "SKIPPED") return { key: "w9.checked_at", time: syncUpdatedAt };
    return null;
}
